/**
 * Central registry of tour anchor targets.
 *
 * Every element the guided tour (or a contextual hint) points at is
 * declared here once and applied as `data-tour={TOUR.x}`. Steps must
 * reference these constants — never inline strings — so a renamed or
 * removed target breaks the build instead of silently producing a tour
 * that spotlights empty space.
 */
export const TOUR = {
  /** Left annotation sidebar (ArgSidebar drawer). */
  sidebar: "sidebar",
  /** A highlight card in the sidebar list (SidebarListItem). */
  annotationCard: "annotation-card",
  /** Category filter select in the sidebar (FilterCategories). */
  categoryFilter: "category-filter",
  /** Category radio list on the highlight-creation tip (Tip). Present
   * only while the tip is expanded, which is exactly when a hint about
   * categories is relevant. */
  categoryPicker: "category-picker",
  /** Reading/canvas view toggle in the navbar (BaseViewToggle). */
  baseViewToggle: "base-view-toggle",
  /** Current reading/canvas dropdown in the navbar (ResourceSelector). */
  resourceSelector: "resource-selector",
  /** Paper editor button in the navbar (PaperEditorButton). */
  paperEditorButton: "paper-editor-button",
  /** Argument canvas surface (Canvas container div). */
  canvasSurface: "canvas-surface",
  /** Evaluation-mode toggle on the canvas (CanvasEvaluationToolbar). */
  evaluationToggle: "evaluation-toggle",
  /** Create-reading control in the resource modal (CreateReadingButton). */
  addReading: "add-reading",
} as const;

export type TourTarget = (typeof TOUR)[keyof typeof TOUR];

/** CSS selector for a tour target, for step definitions and smoke tests. */
export const tourSelector = (target: TourTarget): string =>
  `[data-tour="${target}"]`;
